import { useCallback, useState, useEffect, useRef } from 'react';
import { useChatStore } from '@/stores/chatStore';
import { useAuthStore } from '@/stores/authStore';
import { chatService, StreamMessage, MessageContent } from '@/services/chatService';
import { dispatchChatSessionCreated, dispatchChatSessionUpdated } from '@/hooks/useChatSessions';
import { dispatchArtifactCreated } from '@/services/artifactService';
import type { ChatMessageArtifact, ArtifactType } from '@/types';

interface UseChatOptions {
  projectId?: string;
  initialSessionId?: string | null; // URL에서 전달받은 세션 ID
  onSessionChange?: (sessionId: string | null) => void;
}

export function useChat(projectIdOrOptions?: string | UseChatOptions) {
  const options = typeof projectIdOrOptions === 'string'
    ? { projectId: projectIdOrOptions }
    : projectIdOrOptions || {};
  const { projectId, initialSessionId, onSessionChange } = options;

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const loadedSessionIdRef = useRef<string | null>(null);

  // Get user context from auth store
  const user = useAuthStore((state) => state.user);

  const {
    sessions,
    activeSessionId,
    isStreaming,
    streamingContent,
    thinkingContent,
    setActiveSession,
    addMessage,
    updateMessage,
    setStreaming,
    appendStreamingContent,
    clearStreamingContent,
    appendThinkingContent,
    clearThinkingContent,
    getSessionMessages,
    deleteSession,
    clearSession,
    loadSession,
    loadSessions,
  } = useChatStore();

  const currentSession = sessions.find((s) => s.id === activeSessionId);
  const messages = activeSessionId ? getSessionMessages(activeSessionId) : [];

  // 세션 로드 함수 (내부용)
  const fetchAndLoadSession = useCallback(
    async (sessionId: string): Promise<boolean> => {
      try {
        setIsLoading(true);
        const sessionWithMessages = projectId
          ? await chatService.getSession(projectId, sessionId)
          : await chatService.getGlobalSession(sessionId);
        loadSession(sessionWithMessages);
        setActiveSession(sessionId);
        return true;
      } catch (err) {
        console.error('Failed to load session:', err);
        setError('세션을 불러올 수 없습니다.');
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [projectId, loadSession, setActiveSession]
  );

  // 프로젝트의 모든 세션 목록 로드
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        setIsLoading(true);
        if (projectId) {
          const sessionsList = await chatService.getSessions(projectId);
          loadSessions(sessionsList);
        } else {
          const { data: sessionsList } = await chatService.getGlobalSessions();
          loadSessions(sessionsList);
        }
      } catch (err) {
        console.error('Failed to fetch sessions:', err);
        setError('대화 기록을 불러올 수 없습니다.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSessions();
  }, [projectId, loadSessions]);

  // initialSessionId가 변경되면 해당 세션 로드
  // 참고: initialSessionId가 없다고 해서 activeSessionId를 리셋하면 안 됨!
  // sendMessage에서 새 세션을 생성한 경우 등을 고려해야 함
  useEffect(() => {
    if (!initialSessionId) {
      // 세션 ID가 없으면 별도로 처리하지 않음
      // (activeSessionId를 null로 리셋하면 새로 생성된 세션이 사라짐)
      loadedSessionIdRef.current = null;
      return;
    }

    // 이미 로드한 세션이면 스킵
    if (initialSessionId === loadedSessionIdRef.current) {
      return;
    }

    // 스토어에 이미 메시지가 있는 세션인지 확인
    const existingSession = sessions.find((s) => s.id === initialSessionId);
    if (existingSession && existingSession.messages.length > 0) {
      setActiveSession(initialSessionId);
      loadedSessionIdRef.current = initialSessionId;
      return;
    }

    // 백엔드에서 세션 로드
    loadedSessionIdRef.current = initialSessionId;
    fetchAndLoadSession(initialSessionId);
  }, [initialSessionId, sessions, activeSessionId, setActiveSession, fetchAndLoadSession]);

  // 특정 세션 로드 (외부에서 호출 가능)
  const loadSessionById = useCallback(
    async (sessionId: string) => {
      const success = await fetchAndLoadSession(sessionId);
      if (success) {
        loadedSessionIdRef.current = sessionId;
        onSessionChange?.(sessionId);
      }
    },
    [fetchAndLoadSession, onSessionChange]
  );

  // 새 채팅 시작
  const startNewChat = useCallback(async () => {
    try {
      setIsLoading(true);

      const backendSession = projectId
        ? await chatService.createSession(projectId)
        : await chatService.createGlobalSession();

      const sessionToLoad = { ...backendSession, messages: [] };
      loadSession(sessionToLoad);

      setActiveSession(backendSession.id);
      loadedSessionIdRef.current = backendSession.id;

      if (!projectId) {
        dispatchChatSessionCreated();
      }

      onSessionChange?.(backendSession.id);
      return backendSession;
    } catch (err) {
      console.error('[useChat.startNewChat] Failed to create session:', err);
      setError('채팅 세션을 생성할 수 없습니다.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [projectId, loadSession, setActiveSession, onSessionChange]);

  // 메시지 전송
  const sendMessage = useCallback(
    async (content: MessageContent) => {
      setError(null);
      // 클로저에 캡처된 activeSessionId 대신 최신 스토어 상태 사용
      const currentActiveSessionId = useChatStore.getState().activeSessionId;

      const displayContent = typeof content === 'string'
        ? content
        : content.map(p => p.type === 'text' ? p.text : '[이미지]').join(' ');



      let sessionId = currentActiveSessionId;

      // 활성 세션이 없으면 새로 생성
      if (!sessionId) {
        try {
          const session = await startNewChat();
          sessionId = session.id;
        } catch {
          return;
        }
      }

      addMessage(sessionId, {
        userId: 'user',
        content: displayContent,
        role: 'user',
        projectId,
      });

      // 어시스턴트 응답 플레이스홀더
      const assistantMessageId = addMessage(sessionId, {
        userId: 'assistant',
        content: '',
        role: 'assistant',
        projectId,
        isStreaming: true,
      });

      setStreaming(true);
      clearStreamingContent();
      clearThinkingContent();

      try {
        let fullContent = '';

        if (!projectId) {
          // Global chat - AI 엔진 직접 호출 후 백엔드 저장
          // 첫 번째 사용자 메시지인지 확인 (제목 업데이트 여부 결정)
          const existingMessages = getSessionMessages(sessionId);
          const isFirstUserMessage = existingMessages.filter((m) => m.role === 'user').length <= 1;

          await chatService.addGlobalMessage(sessionId, 'user', displayContent);

          const currentMessages = getSessionMessages(sessionId);
          // Build conversation history excluding the current user message (will be added by sendGlobalMessage)
          // and the placeholder assistant message
          const conversationHistory = currentMessages
            .filter((m) => m.id !== assistantMessageId)
            .slice(0, -1)  // Exclude last message (current user message) to avoid duplication
            .map((m) => ({ role: m.role, content: m.content }));

          // Build user context for AI engine (including LLM preset for model selection)
          const userContext = user ? {
            region: user.region,
            language: user.language,
            name: user.name,
            llmPreset: user.effectiveLlmPreset,
          } : undefined;

          let toolCalls: Array<{ type: string; id?: string; status?: string }> = [];
          const artifacts: ChatMessageArtifact[] = [];

          let streamError: string | null = null;

          await chatService.sendGlobalMessage(
            sessionId,
            content,
            conversationHistory,
            (chunk: StreamMessage) => {
              if (chunk.type === 'start') {
                // Start event - may contain agent info for multi_agent mode
              } else if (chunk.type === 'content' && chunk.content) {
                fullContent += chunk.content;
                appendStreamingContent(chunk.content);
                updateMessage(sessionId!, assistantMessageId, fullContent, undefined, artifacts);
              } else if (chunk.type === 'thinking' && chunk.thinking) {
                // Extended thinking / reasoning streaming
                appendThinkingContent(chunk.thinking);
              } else if (chunk.type === 'tool_call') {
                // Track tool usage (including project tools in multi_agent mode)
                const newToolCall = {
                  type: chunk.toolName || 'unknown',
                  id: chunk.toolData?.id,
                  status: 'executing'
                };
                toolCalls.push(newToolCall);
                // 실시간 UI 업데이트
                updateMessage(sessionId!, assistantMessageId, fullContent, [...toolCalls], artifacts);
              } else if (chunk.type === 'tool_result') {
                // 도구 상태를 completed로 업데이트
                const toolIdx = toolCalls.findIndex(t => t.type === chunk.toolName && t.status === 'executing');
                if (toolIdx >= 0) {
                  toolCalls[toolIdx] = { ...toolCalls[toolIdx], status: 'completed' };
                }
                updateMessage(sessionId!, assistantMessageId, fullContent, [...toolCalls], artifacts);
                // Tool result received - check if it's an artifact creation
                if (chunk.toolName === 'create_artifact' && chunk.toolResult?.success) {
                  const data = chunk.toolResult.data;
                  if (data?.artifact_id) {
                    artifacts.push({
                      id: data.artifact_id,
                      name: data.name || 'Untitled',
                      artifactType: (data.artifact_type || 'generated') as ArtifactType,
                      filePath: data.file_path,
                      createdAt: new Date().toISOString(),
                    });
                    updateMessage(sessionId!, assistantMessageId, fullContent, undefined, artifacts);
                    // 스펙 문서 목록 새로고침 트리거
                    dispatchArtifactCreated({ projectId, artifactType: data.artifact_type });
                  }
                }
              } else if (chunk.type === 'artifact_created') {
                // Direct artifact creation event
                const metadata = chunk.metadata;
                if (metadata?.artifact_id) {
                  artifacts.push({
                    id: metadata.artifact_id,
                    name: metadata.artifact_name || 'Untitled',
                    artifactType: (metadata.artifact_type || 'generated') as ArtifactType,
                    filePath: metadata.file_path,
                    createdAt: new Date().toISOString(),
                  });
                  updateMessage(sessionId!, assistantMessageId, fullContent, undefined, artifacts);
                  // 스펙 문서 목록 새로고침 트리거
                  dispatchArtifactCreated({ projectId, artifactType: metadata.artifact_type as ArtifactType });
                }
              } else if (chunk.type === 'done' && chunk.metadata?.tool_calls) {
                // Capture tool calls from final metadata
                toolCalls = chunk.metadata.tool_calls;
              } else if (chunk.type === 'error') {
                streamError = chunk.error || 'An error occurred';
                setError(streamError);
              }
            },
            userContext
          );



          // Only save assistant message if there was content and no error
          if (streamError) {
            // Display error message in chat
            updateMessage(sessionId, assistantMessageId, `오류가 발생했습니다: ${streamError}`);
            return;
          }

          if (!fullContent.trim()) {
            // No content received, don't save empty message
            updateMessage(sessionId, assistantMessageId, '응답을 받지 못했습니다. 다시 시도해주세요.');
            return;
          }

          await chatService.addGlobalMessage(sessionId, 'assistant', fullContent);

          // 첫 번째 메시지 전송 후 사이드바 세션 목록 새로고침 (제목 업데이트 반영)
          if (isFirstUserMessage) {
            dispatchChatSessionUpdated();
          }
        } else {
          // Project chat - 백엔드 경유
          const projectToolCalls: Array<{ type: string; id?: string; status?: string }> = [];
          const projectArtifacts: ChatMessageArtifact[] = [];

          await chatService.sendMessage(
            projectId,
            sessionId,
            displayContent,
            (chunk: StreamMessage) => {
              if (chunk.type === 'content' && chunk.content) {
                fullContent += chunk.content;
                appendStreamingContent(chunk.content);
                updateMessage(sessionId!, assistantMessageId, fullContent, projectToolCalls, projectArtifacts);
              } else if (chunk.type === 'thinking' && chunk.thinking) {
                appendThinkingContent(chunk.thinking);
              } else if (chunk.type === 'tool_call') {
                projectToolCalls.push({
                  type: chunk.toolName || 'unknown',
                  id: chunk.toolData?.id,
                  status: 'executing'
                });
                updateMessage(sessionId!, assistantMessageId, fullContent, [...projectToolCalls], projectArtifacts);
              } else if (chunk.type === 'tool_result') {
                const toolIdx = projectToolCalls.findIndex(t => t.type === chunk.toolName && t.status === 'executing');
                if (toolIdx >= 0) {
                  projectToolCalls[toolIdx] = { ...projectToolCalls[toolIdx], status: 'completed' };
                }
                // Check for artifact creation
                if (chunk.toolName === 'create_artifact' && chunk.toolResult?.success) {
                  const data = chunk.toolResult.data;
                  if (data?.artifact_id) {
                    projectArtifacts.push({
                      id: data.artifact_id,
                      name: data.name || 'Untitled',
                      artifactType: (data.artifact_type || 'generated') as ArtifactType,
                      filePath: data.file_path,
                      createdAt: new Date().toISOString(),
                    });
                    // 스펙 문서 목록 새로고침 트리거
                    dispatchArtifactCreated({ projectId, artifactType: data.artifact_type });
                  }
                }
                updateMessage(sessionId!, assistantMessageId, fullContent, [...projectToolCalls], projectArtifacts);
              } else if (chunk.type === 'error') {
                setError(chunk.error || 'An error occurred');
              }
            }
          );
        }

        updateMessage(sessionId, assistantMessageId, fullContent);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to send message');
        updateMessage(sessionId, assistantMessageId, '메시지 전송에 실패했습니다. 다시 시도해주세요.');
      } finally {
        setStreaming(false);
        clearStreamingContent();
        clearThinkingContent();
      }
    },
    [
      // activeSessionId 제거됨 - 스토어에서 직접 가져오므로 dependency 불필요
      projectId,
      user,
      addMessage,
      updateMessage,
      setStreaming,
      appendStreamingContent,
      clearStreamingContent,
      appendThinkingContent,
      clearThinkingContent,
      getSessionMessages,
      startNewChat,
    ]
  );

  const selectSession = useCallback(
    (sessionId: string) => {
      setActiveSession(sessionId);
      loadedSessionIdRef.current = sessionId;
      onSessionChange?.(sessionId);
    },
    [setActiveSession, onSessionChange]
  );

  const deleteSessionById = useCallback(
    async (sessionId: string) => {
      try {
        if (projectId) {
          await chatService.deleteSession(projectId, sessionId);
        } else {
          // Global chat session delete if needed
          await chatService.deleteGlobalSession(sessionId);
        }
        deleteSession(sessionId);
        if (sessionId === loadedSessionIdRef.current) {
          loadedSessionIdRef.current = null;
          setActiveSession(null);
          onSessionChange?.(null);
        }
      } catch (err) {
        console.error('Failed to delete session:', err);
        setError('대화 기록을 삭제할 수 없습니다.');
      }
    },
    [projectId, deleteSession, setActiveSession, onSessionChange]
  );

  const clearCurrentSession = useCallback(() => {
    if (activeSessionId) {
      clearSession(activeSessionId);
    }
  }, [activeSessionId, clearSession]);

  const projectSessions = projectId
    ? sessions.filter((s) => s.projectId === projectId)
    : sessions.filter((s) => !s.projectId);

  return {
    sessions: projectSessions,
    currentSession,
    messages,
    isStreaming,
    isLoading,
    streamingContent,
    thinkingContent,
    error,
    sendMessage,
    startNewChat,
    selectSession,
    loadSessionById,
    deleteSessionById,
    clearCurrentSession,
  };
}
